/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import {
  ActionItem,
  LeaseLifecycle,
  Process,
  Unit,
  UserData,
} from '@urbanmix-tech/shared-js';

import { GenericDistinctVisualizer, colorScales } from './BuildingVisualizers';
import type { ColorScaleOptions } from './BuildingVisualizers';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

export type UnitData = {
  unit: Unit;
  daysToExpire: number;
  type: string;
}

export type AggregatedData = {
  grouped: Record<string, {
    units: UnitData[];
    category: number;
  }>;
  categories: number[];
  keys: string[];
}; 


type ExpiringLeasesAggregationProps = {
  tableData : AggregatedData;
  selectedCategoryKeys?: string[];
  onCategoriesSelected? : (keys: string[]) => void;
  onCategoryHover? : (key: string | null) => void;
  showMarket?: boolean;
  onShowMarketChange?: (show: boolean) => void;
  showAffordable?: boolean;
  onShowAffordableChange?: (show: boolean) => void;
};

export function ExpiringLeasesAggregation({
  tableData ,
  selectedCategoryKeys = [],
  onCategoriesSelected,
  onCategoryHover,
  showMarket = true,
  onShowMarketChange,
  showAffordable = true,
  onShowAffordableChange
}: ExpiringLeasesAggregationProps) {

    const [hoveredCategoryKey, setHoveredCategoryKey] = useState<string | null>(null);

  const projectVisualizer = useSelector(
    (state: any) => state.project.projectVisualizer
  );

  const handleCategoryToggle = (key: string, checked: boolean) => {
    const newKeys = checked
      ? [...selectedCategoryKeys, key]
      : selectedCategoryKeys.filter((k) => k !== key);
    onCategoriesSelected?.(newKeys);
  };

  if (!projectVisualizer) {
    return <CircularProgress/>
  } else {
    // console.log('!@#projectVisualizer', projectVisualizer);
  }

  return (
    <div className="unit-mix w-full">
      <div className="relative right-0 top-0 z-10 mb-4 flex flex-row gap-4">
        <div className="flex flex-1 flex-row justify-end gap-4"></div>
      </div>

      {/* interate over tableData.grouped */}
      <div className="">
        <div className="border border-l-0 border-r-0 border-zinc-500">
          <table className="w-full table-auto px-16 py-16">
            <thead>
              <tr>
                <th></th>
                <th>Expires In</th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Market</span>
                    <Checkbox
                      checked={showMarket}
                      onChange={(e) => {
                        // Don't allow unchecking if it's the last one checked
                        if (!e.target.checked && !showAffordable) {
                          return;
                        }
                        onShowMarketChange?.(e.target.checked);
                      }}
                      size="small"
                    />
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Stabilized</span>
                    <Checkbox
                      checked={showAffordable}
                      onChange={(e) => {
                        // Don't allow unchecking if it's the last one checked
                        if (!e.target.checked && !showMarket) {
                          return;
                        }
                        onShowAffordableChange?.(e.target.checked);
                      }}
                      size="small"
                    />
                  </div>
                </th>
                <th>Total</th>
              </tr>
            </thead>
            {tableData && tableData.categories ? (
              <tbody>
                {tableData.keys.map((key) => {
                    
                  const group = tableData.grouped[key];
                //   console.log('group', group,category);

                  if (!group) {
                    return null;
                  }

                  let marketColor = projectVisualizer.getColor(
                    'market_' + group.category
                  );
                  let affordableColor = projectVisualizer.getColor(
                    'affordable_' + group.category
                  );

                  return (
                    <tr key={key} className={twMerge("odd:bg-zinc-900 odd:dark:bg-zinc-800",
                        selectedCategoryKeys.includes(key) ? 'text-red' : '',
                        hoveredCategoryKey == key ? 'bg-blue' : '')}>
                        <th>
                            <Checkbox
                                checked={selectedCategoryKeys.includes(key)}
                                onChange={(e) => {
                                    handleCategoryToggle(key, e.target.checked);
                                }}
                            />
                        </th>
                      <th scope="row" className="">
                        {key}
                      </th>
                      <td className="market min pl-16">
                        <div className="flex flex-row items-center gap-4">
                          <div className="min-w-[20px]">
                            {
                              group.units.filter(
                                (unit: any) => unit.type === 'market'
                              ).length
                            }
                          </div>
                          <div
                            className="h-16 w-16 flex-none"
                            style={{ backgroundColor: marketColor }}
                          />
                        </div>
                      </td>
                      <td className="affordable min-w-[50px] pl-16">
                        <div className="flex flex-row items-center gap-4">
                          <div className="min-w-[20px]">
                            {
                              group.units.filter(
                                (unit: any) => unit.type === 'affordable'
                              ).length
                            }
                          </div>
                          <div
                            className="h-16 w-16 flex-none"
                            style={{ backgroundColor: affordableColor }}
                          />
                        </div>
                      </td>
                      <td className="total">
                        <div className="w-16 flex-none px-2 py-1 text-right">
                          {key === 'rest'
                            ? `${group.units.length.toFixed(0)}`
                            : group.units.length}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={4} className="text-center">
                    <CircularProgress />
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
